import { useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid,
} from "recharts";
import { ChartCard } from "../../../components/ChartCard";
import type { FilterPillSpec } from "../../../components/FilterPills";
import { InfoButton } from "../../../lib/funnel/InfoButton";
import { PRIMARY, MUTED, FG1 } from "../../../lib/funnel/theme";
import { fmtDateShort, fmtN, fmtPct } from "../../../lib/funnel/helpers";
import {
  FEATURE_LABEL, FEATURE_DEFINITION, FEATURE_ORDER,
} from "../definitions";
import type { FeatureKey } from "../definitions";
import type { Entity } from "../helpers";

export type FeatureRow = {
  feature: FeatureKey;
  value: number;
  trend: { label: string; value: number }[];
  momPct: number | null;   // feature MoM WAU growth %
  momDelta: number | null; // feature momPct minus overall momPct (pp)
};

export function FeatureBreakdown({
  rows,
  totalActive,
  entity,
  pagePills,
}: {
  rows: FeatureRow[];
  totalActive: number;
  entity: Entity;
  pagePills: FilterPillSpec[];
}) {
  const [expanded, setExpanded] = useState<FeatureKey | null>(null);
  const noun = entity === "user" ? "users" : "companies";
  const ordered = FEATURE_ORDER
    .map((key) => rows.find((r) => r.feature === key))
    .filter((r): r is FeatureRow => !!r);

  return (
    <ChartCard
      title="Feature breakdown (this week)"
      subtitle={`Distinct active ${noun} per feature for the current week. Click a row to see its trend. Each row's denominator is the overall active ${noun} count for the week (${fmtN(totalActive)}).`}
      filterPills={pagePills}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "0 4px 4px", borderBottom: "1px solid var(--border-default)",
        }}>
          <span style={{ width: 130, flexShrink: 0 }} />
          <span style={{ flex: 1 }} />
          <span style={{ width: 130, flexShrink: 0, textAlign: "right", fontSize: 10, color: MUTED }}>active {noun}</span>
          <span style={{ width: 60, flexShrink: 0, textAlign: "right", fontSize: 10, color: MUTED }}>MoM</span>
          <span style={{ width: 80, flexShrink: 0, textAlign: "right", fontSize: 10, color: MUTED }}>vs platform</span>
        </div>
        {ordered.map((r) => {
          const isOpen = expanded === r.feature;
          return (
            <div
              key={r.feature}
              style={{ borderBottom: "1px solid var(--border-default)", paddingBottom: 6 }}
            >
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : r.feature)}
                aria-expanded={isOpen}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  width: "100%", padding: "6px 4px", border: "none",
                  background: "transparent", cursor: "pointer", textAlign: "left",
                }}
              >
                <span style={{
                  display: "flex", alignItems: "center", gap: 6,
                  width: 130, flexShrink: 0, fontSize: 12, color: FG1, fontWeight: 600,
                }}>
                  <i className={`ti ti-chevron-${isOpen ? "down" : "right"}`} style={{ fontSize: 12, color: MUTED }} />
                  {FEATURE_LABEL[r.feature]}
                  <InfoButton align="left" width={320} label={`${FEATURE_LABEL[r.feature]} definition`}>
                    {FEATURE_DEFINITION[r.feature]}
                  </InfoButton>
                </span>
                <span style={{ flex: 1, height: 16, background: "var(--bg-2)", borderRadius: 3, position: "relative", overflow: "hidden" }}>
                  <span style={{
                    position: "absolute", left: 0, top: 0, bottom: 0,
                    width: `${totalActive === 0 ? 0 : Math.min(100, (r.value / Math.max(totalActive, 1)) * 100)}%`,
                    background: PRIMARY, borderRadius: 3,
                  }} />
                </span>
                <span style={{ width: 130, flexShrink: 0, textAlign: "right", fontSize: 11, color: MUTED }}>
                  {fmtN(r.value)} ({fmtPct(r.value, totalActive)})
                </span>
                <span style={{ width: 60, flexShrink: 0, textAlign: "right", fontSize: 11, fontWeight: 600, color: momColor(r.momPct) }}>
                  {fmtMom(r.momPct)}
                </span>
                <span style={{ width: 80, flexShrink: 0, textAlign: "right", fontSize: 11, fontWeight: 600, color: momColor(r.momDelta) }}>
                  {fmtDelta(r.momDelta)}
                </span>
              </button>
              {isOpen && <FeatureMiniTrend points={r.trend} />}
            </div>
          );
        })}
      </div>
    </ChartCard>
  );
}

function momColor(v: number | null): string {
  if (v == null) return MUTED;
  if (v > 1) return "var(--fg-success)";
  if (v < -1) return "var(--fg-danger)";
  return MUTED;
}

function fmtMom(v: number | null): string {
  if (v == null) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${Math.round(v)}%`;
}

function fmtDelta(v: number | null): string {
  if (v == null) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${Math.round(v)}pp`;
}

function FeatureMiniTrend({ points }: { points: { label: string; value: number }[] }) {
  const data = points.map((p) => ({ ...p, displayLabel: fmtDateShort(p.label) }));
  return (
    <div style={{ padding: "6px 0 8px 130px" }}>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
          <XAxis dataKey="displayLabel" tick={{ fontSize: 9, fill: MUTED }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 9, fill: MUTED }} allowDecimals={false} width={28} />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="value"
            stroke={PRIMARY}
            strokeWidth={1.5}
            dot={{ r: 2, fill: PRIMARY }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// HBar variant kept for potential reuse; current layout uses the inline bars
// above for a tighter row, but the recharts version is here if needed.
export function FeatureHBar({
  rows,
  totalActive,
  pagePills,
}: {
  rows: FeatureRow[];
  totalActive: number;
  pagePills: FilterPillSpec[];
}) {
  const data = rows.map((r) => ({ label: FEATURE_LABEL[r.feature], value: r.value }));
  return (
    <ChartCard title="Feature breakdown" subtitle="WAU per feature." filterPills={pagePills}>
      <ResponsiveContainer width="100%" height={Math.max(160, data.length * 32)}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 80, bottom: 4, left: 10 }}>
          <XAxis type="number" hide />
          <YAxis dataKey="label" type="category" tick={{ fontSize: 11, fill: FG1 }} width={120} interval={0} />
          <Tooltip
            formatter={(v) => {
              const n = Number(v);
              return [`${fmtN(n)} (${fmtPct(n, totalActive)})`, "Active"] as [string, string];
            }}
          />
          <Bar dataKey="value" fill={PRIMARY} radius={[0, 3, 3, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
